import { Page } from "types";

/**
 * Gibt die Schnittmenge zwischen zwei Mengen zurück.
 *
 * @param A - Eine Menge.
 * @param B - Eine Menge.
 * @returns Die Schnittmenge der zwei Mengen.
 */
const intersect = <T>(A: Set<T>, B: Set<T>) =>
  new Set(Array.from(A).filter((element) => B.has(element)));

/**
 * Gibt die Kardinalzahl (Anzahl der Elemente) einer Menge zurück.
 *
 * @param set - Eine Menge.
 * @returns Die Kardinalzahl der Menge.
 */
const cardinality = <T>(set: Set<T>) => set.size;

/**
 * Zerlegt eine Zeichenkette in eine Menge überlappender Zeichenpaare
 * (Bigramme).
 *
 * @remarks
 * Bei der Erstellung von Bigrammen wird ein imaginäres Fenster der Größe von
 * zwei Zeichen jeweils iterativ um ein Zeichen nach rechts bewegt.
 *
 * Befindet sich ein Zeichenpaar während einer Iteration innerhalb des
 * imaginären Fensters, so wird dieses zur Menge der Bigramme hinzugefügt.
 *
 * Stößt das imaginäre Fenster mit dem rechten Rahmen an das Ende der
 * Zeichenkette, wird die Iteration terminiert, denn es können keine Bigramme
 * mehr gebildet werden.
 *
 * _Beachte:_ In der Menge der Bigramme ist kein Bigramm mehrfach enthalten.
 *
 * @example
 * Das nachfolgende Beispiel verwendet die Zeichenkette `nacht` zur Erstellung
 * der Bigramme.
 *
 * ```text
 * Menge der Bigramme nach jeder Iteration.
 *
 *                     +--+
 *   1. Iteration      |na|cht      { na }
 *                     +--+
 *
 *                      +--+
 *   2. Iteration      n|ac|ht      { na, ac }
 *                      +--+
 *
 *                       +--+
 *   3. Iteration      na|ch|t      { na, ac, ch }
 *                       +--+
 *
 *                        +--+
 *   4. Iteration      nac|ht|      { na, ac, ch, ht }
 *                        +--+
 * ```
 *
 * Mit der vierten Iteration hat der Rahmen des imaginären Fensters das Ende der
 * Zeichenkette erreicht und die Iteration terminiert.
 *
 * Somit umfasst die Menge der Bigramme folgende Elemente: `{ na, ac, ch, ht }`
 *
 * @param string - Eine Zeichenkette.
 * @returns Die Menge der Bigramme.
 */
const bigrams = (string: string): Set<string> => {
  /* Entferne Leerzeichen und ignoriere Groß-/Kleinschreibung. */
  const current = string.trim().toLowerCase();

  /* Keine Erzeugung von Bigrammen möglich. */
  if (current.length < 2) {
    return new Set();
  }

  /* Erzeuge ein Bigramm. */
  const bigram = current.substring(0, 2);

  /* Verschiebe um ein Zeichen. */
  const next = current.slice(1);

  /* Gebe die Menge der Bigramme rekursiv zurück. */
  return bigrams(next).add(bigram);
};

/**
 * Berechnet den Dice-Koeffizienten zweier Zeichenketten.
 *
 * @remarks
 * Er misst die prozentuale Übereinstimmung zweier Zeichenketten.
 *
 * Je höher der Wert, desto höher die Übereinstimmung.
 * Je geringer der Wert, desto geringer die Übereinstimmung.
 *
 * @example
 * Das nachfolgende Beispiel ermittelt keine Übereinstimmung zweier
 * Zeichenketten.
 *
 * ```typescript
 * const x = "Nacht";
 * const y = "Nebel";
 *
 * const similarity = diceCoefficient(x, y);
 *
 * console.log(similarity); // 0 -> Keine Übereinstimmung
 * ```
 *
 * @example
 * Das nachfolgende Beispiel ermittelt eine geringe Übereinstimmung zweier
 * Zeichenketten.
 *
 * ```typescript
 * const x = "Nacht";
 * const y = "Natur";
 *
 * const similarity = diceCoefficient(x, y);
 *
 * console.log(similarity); // 0.25 -> Geringe Übereinstimmung
 * ```
 *
 * @example
 * Das nachfolgende Beispiel ermittelt eine hohe Übereinstimmung zweier
 * Zeichenketten.
 *
 * ```typescript
 * const x = "Nacht";
 * const y = "Wacht";
 *
 * const similarity = diceCoefficient(x, y);
 *
 * console.log(similarity); // 0.75 -> Hohe Übereinstimmung
 * ```
 *
 * @example
 * Das nachfolgende Beispiel ermittelt eine exakte Übereinstimmung zweier
 * Zeichenketten.
 *
 * ```typescript
 * const x = "Nacht";
 * const y = "Nacht";
 *
 * const similarity = diceCoefficient(x, y);
 *
 * console.log(similarity); // 1 -> Exakte Übereinstimmung
 * ```
 *
 * @param x - Eine Zeichenkette.
 * @param y - Eine Zeichenkette.
 * @returns Die prozentuale Übereinstimmung zweier Zeichenketten.
 */
const diceCoefficient = (x: string, y: string) => {
  const bigramsX = bigrams(x);
  const bigramsY = bigrams(y);

  const numerator = 2 * cardinality(intersect(bigramsX, bigramsY));
  const denominator = cardinality(bigramsX) + cardinality(bigramsY);

  return numerator / denominator;
};

/**
 * Erstellt Suchergebnisse unter Verwendung der unscharfen Suche.
 *
 * @remarks
 * > Der Begriff _unscharfe Suche_ bedeutet, dass Suchergebnisse auch dann
 * > gefunden werden, wenn der Suchbegriff einen oder mehrere Rechtschreibfehler
 * > enthält.
 *
 * Bei der unscharfen Suche werden jene Seiten als Suchergebnisse gewertet, die
 * mindestens eine der folgenden Kriterien erfüllen:
 *
 *   1. Der Suchbegriff und der Titel der Seite müssen eine gewisse prozentuale
 *      Mindestübereinstimmung aufweisen, die durch den Parameter `threshold`
 *      festgelegt wird.
 *
 *   2. Der Suchbegriff kommt teilweise oder vollständig im Titel der Seite vor.
 *
 * Jene Seiten, deren Titel keine der obigen Kriterien erfüllen, werden
 * ignoriert.
 *
 * **Optimierung der Qualität und Quantität der Suchergebnisse**
 *
 * Durch Setzen des Parameters `threshold` kann entweder die Qualität oder die
 * Quantität der Suchergebnisse optimiert werden.
 *
 * Die sich daraus ergebenden Wechselwirkungen sind in der nachstehenden Tabelle
 * aufgeführt.
 *
 * ```text
 * +-----------+----------+-----------+
 * | Threshold | Qualität | Quantität |
 * +-----------+----------+-----------+
 * |     +     |     +    |     -     |
 * +-----------+----------+-----------+
 * |     -     |     -    |     +     |
 * +-----------+----------+-----------+
 * ```
 *
 * @example
 * Das nachfolgende Beispiel erzeugt Suchergebnisse für einen Suchbegriff, der
 * Rechtschreibfehler enthält.
 *
 * ```typescript
 * // Definiere den Suchindex.
 * const index = [{
 *     title: "Asymmetric Encryption",
 *     path: "/asymmetric-encryption",
 *     date: "2022-01-01"
 * }, {
 *     title: "Symmetric Encryption",
 *     path: "/symmetric-encryption",
 *     date: "2022-01-01"
 * }, {
 *     title: "Levenshtein-Distance",
 *     path: "/levenshtein-distance",
 *     date: "2022-12-24"
 * }, {
 *     title: "Dice-Coefficient",
 *     path: "/dice-coefficient",
 *     date: "2022-12-24"
 * }];
 *
 * // Definiere den Suchbegriff.
 * const query = "Asmtric Encrptin";
 *
 * // Erstelle die Suchergebnisse unter der Verwendung der unscharfen Suche.
 * const results = search(query, index, 0.39);
 *
 * // Gebe die Suchergebnisse zurück.
 * console.log(results);
 * ```
 *
 * Obwohl der Suchbegriff mehrere gravierende Rechtschreibfehler enthält, ist
 * die unscharfe Suche dennoch in der Lage, die korrekten Ergebnisse
 * zurückzugeben.
 *
 * ```json
 * [
 *   {
 *     "page": {
 *       "date": "2022-01-01",
 *       "path": "/asymmetric-encryption",
 *       "title": "Asymmetric Encryption"
 *     },
 *     "similarity": 0.6060606060606061
 *   },
 *   {
 *     "page": {
 *       "date": "2022-01-01",
 *       "path": "/symmetric-encryption",
 *       "title": "Symmetric Encryption"
 *     },
 *     "similarity": 0.5625
 *   }
 * ]
 * ```
 *
 * @param query - Ein Suchbegriff.
 * @param index - Ein Suchindex.
 * @param threshold - Eine Untergrenze für die Mindestübereinstimmung,
 *                    in Prozent.
 * @returns Die Suchergebnisse.
 *
 * @public
 */
const search = (query: string, index: Array<Page>, threshold = 0.39) => {
  /* Entferne Leerzeichen und ignoriere Groß-/Kleinschreibung. */
  query = query.trim().toLowerCase();

  const results = index.reduce((results, page) => {
    /* Messe die prozentuale Übereinstimmung der Zeichenketten. */
    const similarity = diceCoefficient(query, page.title);

    /* Prüfe, ob es sich um ein Suchergebnis handelt. */
    if (similarity >= threshold || page.title.toLowerCase().includes(query)) {
      results.push({ page, similarity });
    }

    /* Gebe die Liste der Suchergebnisse zurück. */
    return results;
  }, new Array<{ similarity: number; page: Page }>());

  /* Sortiere die Suchergebnisse in absteigender Reihenfolge. */
  return results.sort((first, second) => second.similarity - first.similarity);
};

export { intersect, cardinality, bigrams, search };
